import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  INotificationBaseMessage,
  IPayloadUpdatedShowInfoLibrary,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { ILibraryCategory, showInfoLibraryApi } from "api/showInfoLibrary";
import { registerStore } from "store/utils/reset-store.utils";

const LISTENER_ID = "SHOW_INFO_LIBRARY_STORE";

type State = {
  /** Keyed by organiser company: the library is company-scoped, not per Show. */
  items: Record<string, ILibraryCategory[]>;
  isLoading: boolean;
  /** Companies with a read in flight, so one does not block another. */
  loadingFor: string[];
};

type Action = {
  fetch: (organiserCompanyId: string, force?: boolean) => Promise<void>;
  update: (organiserCompanyId: string) => Promise<void>;
  autoCatch: () => () => void;
};

export const useShowInfoLibrary = create(
  devtools<State & Action>((set, get) => ({
    items: {},
    isLoading: false,
    loadingFor: [],

    /**
     * The library is company-scoped, so this subscribes without a `showId` —
     * the same shape `build-type-library.store.ts` uses. Returns the
     * unsubscribe so the owning page can clean up from its effect.
     */
    autoCatch: () => {
      apiV2.notification.subscribe<
        INotificationBaseMessage<IPayloadUpdatedShowInfoLibrary>
      >({
        key: NotificationType.UPDATED_SHOW_INFO_LIBRARY,
        listenerId: LISTENER_ID,
        callback: async ({ payload }) => {
          // Only refresh a company already in the map: an event for a company
          // this session never loaded is not ours to fetch.
          if (payload && get().items[payload.organiserCompanyId]) {
            await get().update(payload.organiserCompanyId);
          }
        },
      });

      return () =>
        apiV2.notification.unsubscribe({
          key: NotificationType.UPDATED_SHOW_INFO_LIBRARY,
          listenerId: LISTENER_ID,
        });
    },

    update: async (organiserCompanyId) => {
      try {
        // Empty categories included: `Add Section` needs them in its select.
        const categories = await showInfoLibraryApi.getMany({
          organiserCompanyId,
        });
        set((state) => ({
          items: { ...state.items, [organiserCompanyId]: categories },
        }));
      } catch (e) {
        console.error("show-info-library store:", e);
      }
    },

    /*
     * `in` and not `items[id]`: an empty library reads as `[]`, which is truthy,
     * so it would never be read again. In flight is tracked per company, since
     * one flag let one company's request block another's first read.
     */
    fetch: async (organiserCompanyId, force = false) => {
      const { items, loadingFor, update } = get();

      if (loadingFor.includes(organiserCompanyId)) return;
      if (!force && organiserCompanyId in items) return;

      set((state) => ({
        loadingFor: [...state.loadingFor, organiserCompanyId],
        isLoading: true,
      }));

      try {
        await update(organiserCompanyId);
      } finally {
        set((state) => {
          const loading = state.loadingFor.filter(
            (id) => id !== organiserCompanyId
          );

          return { loadingFor: loading, isLoading: !!loading.length };
        });
      }
    },
  }))
);

registerStore(useShowInfoLibrary);
